// Reale Kapazität eines ZFS-Pools statt Rohkapazität.
//
// `zpool list` (size/alloc/free) zählt Paritätsblöcke mit: ein RAIDZ2
// aus sechs Platten meldet 27,3 TB, obwohl nur ~18 TB für Daten da
// sind — zwei Platten gehen für den Ausfallschutz drauf. `zfs list` auf
// dem Wurzel-Dataset kennt die nutzbare Zahl; der Server reicht sie als
// usable_* durch.
//
// Bis zum 04.08.2026 standen beide Sichten unkommentiert nebeneinander:
// die Kopfzeile zeigte "16,4 TB / 28,2 TB" (roh), die Pool-Zeile
// darunter "10,9 TB belegt, 7,15 TB frei" (nutzbar). Zwei Zahlenpaare
// in derselben Optik, die sich zu widersprechen schienen.
//
// Diese Helfer stehen bewusst zentral: die Rückfall-Regel wird an
// mehreren Stellen gebraucht (Seite, Summary-Store), und zwei Kopien
// hätten irgendwann verschiedene Antworten gegeben.

// Absichtlich ein strukturelles Minimum statt ZfsPool: der
// Summary-Store führt eine eigene, schlanke Pool-Schnittstelle, und die
// sollte nicht den vollen Typ mitschleppen müssen, nur um hier
// hineinzupassen.
export interface PoolCapacity {
  size: number;
  alloc: number;
  free: number;
  usable_size: number;
  usable_used: number;
  usable_free: number;
  overhead: number;
}

/** Nutzbare Gesamtkapazität. Rückfall auf roh, wenn kein Wurzel-Dataset
 *  gemeldet wurde (z. B. ein Host mit altem Agenten) — lieber die grobe
 *  Zahl als eine erfundene Null, die als "Pool ist leer" zu lesen wäre. */
export function usableSize(p: PoolCapacity): number {
  return p.usable_size > 0 ? p.usable_size : p.size;
}

export function usableUsed(p: PoolCapacity): number {
  return p.usable_size > 0 ? p.usable_used : p.alloc;
}

export function usableFree(p: PoolCapacity): number {
  return p.usable_size > 0 ? p.usable_free : p.free;
}

/** Füllgrad in Prozent, bezogen auf die nutzbare Kapazität. Das ist die
 *  Zahl, die jemand meint, der eine Warnschwelle auf 80 % setzt. */
export function usablePct(p: PoolCapacity): number {
  const size = usableSize(p);
  return size > 0 ? (usableUsed(p) / size) * 100 : 0;
}

/** Anteil der Rohkapazität, der für Ausfallschutz und ZFS-Reserve
 *  abgeht. 0, wenn unbekannt. */
export function overheadPct(p: PoolCapacity): number {
  if (p.size <= 0 || p.overhead <= 0) return 0;
  return Math.round((p.overhead / p.size) * 100);
}
